import React, { ReactNode } from 'react';
import { Box, SxProps, Theme } from '@mui/material';

// Type définissant les propriétés du composant GlowBox
type Props = {
  children: ReactNode;
  actif?: boolean;
  color?: string;
  sx?: SxProps<Theme>;
}

/**
 * GlowBox - Un composant qui entoure ses enfants avec un effet de surbrillance personnalisable
 * 
 * @param children - Le contenu à afficher à l'intérieur de la box
 * @param actif - Indique si l'effet de surbrillance est actif (défaut: true)
 * @param color - La couleur de l'effet de surbrillance (défaut: '#3f51b5')
 * @param sx - Styles MUI sx additionnels à appliquer à la box
 */
export default function GlowBox({ 
  children, 
  actif = true, 
  color = '#3f51b5',
  sx = {} 
} : Props) {
  return (
    <Box
      sx={{
        position: 'relative',
        padding: 1,
        borderRadius: 1,
        transition: 'all 0.3s ease',
        boxShadow: actif ? `0 0 10px 2px ${color}` : 'none',
        border: actif ? `1px solid ${color}` : '1px solid transparent',
        ...sx
      }}
    >
      {children}
    </Box>
  );
}

/**
 * Exemples d'utilisation:
 *
 * // Exemple de base
 * <GlowBox>
 *   <p>Contenu avec une surbrillance bleue par défaut</p>
 * </GlowBox>
 *
 * // Avec une couleur personnalisée
 * <GlowBox color="#ff5722">
 *   <p>Contenu avec une surbrillance orange</p>
 * </GlowBox>
 *
 * // Avec la surbrillance désactivée (contrôlée par une variable)
 * const [estActif, setEstActif] = useState(true);
 * <GlowBox actif={estActif}>
 *   <p>Surbrillance qui peut être activée/désactivée</p>
 * </GlowBox>
 *
 * // Avec des styles supplémentaires
 * <GlowBox sx={{ backgroundColor: '#f5f5f5', marginTop: 2 }}>
 *   <p>Contenu avec un fond personnalisé et une marge</p>
 * </GlowBox>
 */